// eslint-disable-next-line simple-import-sort/imports
import "init.js";

import React from "react";
import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
// import { hot } from "react-hot-loader";
import { hotjar } from "react-hotjar";
import { useTranslation } from "react-i18next";
import { css, Global } from "@emotion/react";
import { ViewportContextProvider } from "@monorepo/components";
import { ANALYTICS_COOKIE_NAME, ENVIRONMENT, ENVIRONMENT_STRING } from "@monorepo/constants";
import { getCookieValue, getIsAnalyticsCookieAccepted, valueExists } from "@monorepo/helpers";
import theme from "@monorepo/styles/theme";
import { EnvCodeEnum } from "@monorepo/types";
import { Typography } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { createBrowserHistory } from "history";
import { Routes } from "routes";

import { AnalyticsCookieBanner } from "components/AnalyticsCookieBanner/AnalyticsCookieBanner";
import { IdleTimeChecker } from "components/IdleTimeChecker/IdleTimeChecker";

import "@monorepo/helpers/src/yup-extended";

import { Main, StyledToastContainer } from "./App.styles";

import "react-toastify/dist/ReactToastify.css";

const hotjarOptions = {
  id: Number(import.meta.env.VITE_HOTJAR_ID),
  sv: 6,
};

const isAnalyticsCookieAccepted = getIsAnalyticsCookieAccepted();
if (isAnalyticsCookieAccepted) {
  hotjar.initialize(hotjarOptions);
}

let ga4MeasurementId;
let gtmId;
switch (ENVIRONMENT) {
  case EnvCodeEnum.SANDBOX:
    ga4MeasurementId = String(import.meta.env.VITE_GOOGLE_ANALYTICS_ID_TEST);
    gtmId = String(import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID_TEST);
    break;
  case EnvCodeEnum.TEST:
    ga4MeasurementId = String(import.meta.env.VITE_GOOGLE_ANALYTICS_ID_TEST);
    gtmId = String(import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID_TEST);
    break;
  case EnvCodeEnum.STAGING:
    ga4MeasurementId = String(import.meta.env.VITE_GOOGLE_ANALYTICS_ID_STAGING);
    gtmId = String(import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID_STAGING);
    break;
  case EnvCodeEnum.LOCAL:
    ga4MeasurementId = String(import.meta.env.VITE_GOOGLE_ANALYTICS_ID_DEV);
    gtmId = String(import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID_DEV);
    break;
  default:
    ga4MeasurementId = String(import.meta.env.VITE_GOOGLE_ANALYTICS_ID_PROD);
    gtmId = String(import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID_PROD);
}

const tagManagerArgs = {
  gtmId,
  dataLayerName: "dataLayer",
};

// TODO PERLGO-4029 maybe also initialize these only if cookies_accept===true?
TagManager.initialize(tagManagerArgs);
ReactGA.initialize(ga4MeasurementId);

const history = createBrowserHistory();

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    new Integrations.BrowserTracing({
      tracingOrigins: [
        "test-api-public.perlas.lt",
        "sandbox-api-public.perlas.lt",
        "api-public.dataop.lt",
        "staging-api-public.dataop.lt",
        /^\//,
      ],
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
    }),
  ],
  normalizeDepth: 10,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  tracesSampleRate: 0.0,
  environment: ENVIRONMENT_STRING,
});

export const App: React.FC = () => {
  const { t } = useTranslation();
  const isAnalyticsCookieSet = valueExists(getCookieValue(ANALYTICS_COOKIE_NAME));

  return (
    <ViewportContextProvider isThirdParty={false}>
      <ThemeProvider theme={theme}>
        <CssBaseline>
          <Global
            styles={css`
              @media screen and (max-width: ${theme.breakpoints.values.md}px) {
                a.chat-btn {
                  bottom: 70px;
                }
              }
            `}
          />
          <Sentry.ErrorBoundary
            fallback={({ error, componentStack }) => (
              <>
                <Typography component="div">{t("common.you_have_encountered_error")}</Typography>
                <Typography component="div">{error.toString()}</Typography>
                <Typography component="div">{componentStack}</Typography>
              </>
            )}
          >
            <Main>
              <Routes />
              {!isAnalyticsCookieSet ? <AnalyticsCookieBanner /> : null}
            </Main>
            <IdleTimeChecker />
            <StyledToastContainer
              limit={5}
              position="top-center"
              autoClose={5000}
              newestOnTop={false}
              hideProgressBar
              closeOnClick
              rtl={false}
              pauseOnHover
            />
          </Sentry.ErrorBoundary>
        </CssBaseline>
      </ThemeProvider>
    </ViewportContextProvider>
  );
};

export default App;
